import * as THEOS from "../interfaces/theos.interface";
import { IErrorMessage, IFeatureData, IGameSettings, IRoundFinishedData, IRoundStartedData, IWinnings } from "./types";
import { _configuration } from "./configuration";
import { getWindowParameter } from "../utils/urlParameters";
import { openNewTab } from "../utils/openNewTab";
import { postToOperator } from "./postToOperator";
import { sendGameEvent } from "./sendGameEvent";

/**
 * Will send an action postMessage if the given target matches the pattern.
 * 
 * @returns false if an action was not sent (not a valid action).
 * 
 * @param target Target string, such as &homeurl value
 */
const postAction = (target?: string): boolean =>
{
    if (target === undefined || target === "") {
        return false;
    }

    const ACTION_PREFIX = "action:";

    if (!target.startsWith(ACTION_PREFIX)) {
        return false;
    }

    // URL is an action - send an action postMessage
    const action = target.slice(ACTION_PREFIX.length);
    sendGameEvent(THEOS.GameEvent.USER_ACTION, {action});

    return true;
};

/**
 * Report that the game has started loading.
 */
export const gameLoadStarted = (): void =>
{
    sendGameEvent(THEOS.GameEvent.GAME_LOAD_STARTED);
};

/**
 * Send game load progress.
 * @param progress - Game loading progress, between 0 and 100
 */
export const gameLoadProgress = (progress: number): void =>
{
    sendGameEvent(THEOS.GameEvent.GAME_LOAD_PROGRESS, progress);
};

/**
* Report that the game has finished loading.
*/
export const gameLoadCompleted = (): void =>
{
    sendGameEvent(THEOS.GameEvent.GAME_LOAD_COMPLETED);
};

/**
 * Report that a game round has started animating.
 * @param roundStartedData - Optional data containing balance information when a round starts. Can be omitted if balance is already being sent using send.[[balanceUpdate]]
 */
export const roundStarted = (roundStartedData?: IRoundStartedData): void =>
{
    sendGameEvent(THEOS.GameEvent.ROUND_STARTED, roundStartedData);
};

/**
 * Reports that the game round animations have completed, and final balance is shown.
 * @param roundFinishedData Balance and win data. Can be omitted if both are already being sent using send.[[balanceUpdate]] and send.[[winUpdate]]
 */
export const roundFinished = (roundFinishedData?: IRoundFinishedData): void =>
{
    sendGameEvent(THEOS.GameEvent.ROUND_FINISHED, roundFinishedData);
};

/**
 * Report that a feature (bonus game) is a starting.
 * In some jurisdictions the game will be paused with [[freeze]] as a response, and continued once the player has confirmed it.
 * (see [[handleFeaturePause]])
 * @param featureData Feature details, such as type. Optional, can be used to fine tune the response to this event.
 */
export const featureStarted = (featureData?: IFeatureData): void =>
{
    sendGameEvent(THEOS.GameEvent.FEATURE_STARTED, featureData);
};

/**
 * Report that a feature (bonus game) has finished.
 * @param featureData Feature details, such as type. Optional, can be used to fine tune the response to this event.
 */
export const featureFinished = (featureData?: IFeatureData): void =>
{
    sendGameEvent(THEOS.GameEvent.FEATURE_FINISHED, featureData);
};

/**
 * Report that an autoplay session has started.
 */
export const autoPlayStarted = (): void =>
{
    sendGameEvent(THEOS.GameEvent.AUTOPLAY_STARTED);
};

/**
 * Report that an autoplay session has ended.
 */
export const autoPlayFinished = (): void =>
{
    sendGameEvent(THEOS.GameEvent.AUTOPLAY_FINISHED);
};

/**
 * Sends a balance update to operator window.
 *
 * send.[[roundStarted]] and send.[[roundFinished]] will automatically send this if their balance fields are valid (not negative).
 *
 * on.[[refreshBalance]] needs to be answered with this.
 *
 * @param balance - New balance, in cents
 */
export const balanceUpdate = (balance: number): void =>
{
    sendGameEvent(THEOS.GameEvent.BALANCE_UPDATE, balance);
};

/**
 * Report a stake/bet amount change.
 * @param bet - The new bet amount, in cents
 */
export const betUpdate = (bet: number): void =>
{
    sendGameEvent(THEOS.GameEvent.BET_UPDATE, bet);
};

/**
 * Report a win.
 *
 * `send.roundFinished` will send this automatically if it has a valid winnings field.
 *
 * A win update must be sent for every played round, even if the win is 0.
 *
 * @param win - Win amount data
 */
export const winUpdate = (win?: IWinnings): void =>
{
    sendGameEvent(THEOS.GameEvent.WIN_UPDATE, win);
};

/**
 * Report a setting change.
 *
 * @param settings - An object containing one ore more changed settings
 */
export const updateSettings = (settings: IGameSettings): void =>
{
    sendGameEvent(THEOS.GameEvent.UPDATE_SETTINGS, settings);
};

/**
 * Send an error message for the operator to show.
 *
 * If you do not want to use [[showError]], you can use this to send the error message directly.
 *
 * Use on.[[errorMessageDismissed]] to detect when the popup is closed in the operator window.
 *
 * Use on.[[errorMessageDisplayed]] if you need to know if a popup was displayed in the operator window.
 *
 * [[operatorHandlesErrors]] in [[getConfiguration]] return data is true if the environment is using remote error messages.
 *
 * @param message - The error message, in the format it is received from the Casino server
 *
 * @returns False if the operator is not handling error popups, and they should be shown in game ([[getConfiguration]] returns [[operatorHandlesErrors]] as false).
 */
export const errorMessage = (message: IErrorMessage): boolean =>
{
    sendGameEvent(THEOS.GameEvent.ERROR_MESSAGE, message);

    if (!_configuration.operatorHandlesErrors) {
        return false;
    }

    return true;
};

/**
 * Navigates the game window to a new page.
 * 
 * Also handles the `action:` prefix.
 * 
 * Note: [[exitGame]] will navigate to `&homeurl` automatically, so it's possible you never need to use this function directly.
 * 
 * @param target Navigate target - url or user action
 * @returns true if the parameter was defined and not empty
 */
export const navigate = (target?: string): boolean =>
{
    if (target === undefined || target === "") {
        return false;
    }

    if (postAction(target)) {
        return true;
    }

    window.location.href = target;
    return true;
};

/**
 * Request game exit. Will notify the operator, redirect the game window to `&homeurl`, or move back in browser history if neither
 * is applicable. Also handles the 'buttonEventsIframe' setting of client config.
 *
 * Called for example when the home button is clicked on mobile, or when the game is closed via reality check.
 *
 * @returns True if succesful
 */
export const exitGame = (): boolean =>
{
    sendGameEvent(THEOS.GameEvent.EXIT_GAME);

    if (!_configuration.operatorHandlesGameExit) {
        const homeurl = getWindowParameter("homeurl");

        if (navigate(homeurl)) {
            return true;
        }

        if (_configuration.clientConfig?.buttonEventsIframe) {
            postToOperator("closeGame");

            return true;
        }

        if (homeurl === undefined || homeurl === "") {
            if (window.history.length > 1) {
                window.history.back();

                return true;
            }
            
            return false;
        }
    }

    return true;
};

/**
 * Show history. Will notify the operator or open a new tab to RC history URL.
 * 
 * Must be called directly from a user interaction handler (e.g. a click event), otherwise the popup might get blocked.
 */
export const showHistory = (): void =>
{
    const rcHistoryUrl = getWindowParameter("rchistoryurl");

    if (postAction(rcHistoryUrl)) {
        return;
    }

    if (openNewTab(rcHistoryUrl)) {
        return;
    }

    if (_configuration.clientConfig?.buttonEventsIframe) {
        postToOperator("showHistory");
    }
};

/**
 * @hidden
 * Go to a game, if the operator supports it. This game will be closed if successful.
 * 
 * Using this is optional.
 */
export const goToGame = (gameRef: string): boolean =>
{
    sendGameEvent(THEOS.GameEvent.GOTO_GAME, gameRef);

    // TODO: check operator support
    return true;
};

/**
 * Open a quick deposit, if the operator supports it.
 *
 * Using this is optional. Can be sent for example when the in-game out of funds error message is closed.
 *
 * The operator is expected to dispatch [[refreshBalance]] if a deposit is made.
 *
 * If the operator does not support this, nothing will happen
 */
export const openQuickDeposit = (): void =>
{
    sendGameEvent(THEOS.GameEvent.OPEN_QUICK_DEPOSIT);
};